import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Pagination } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PortalAnnouncements from '../components/Home/PortalAnnouncements';
import IELTSDashboardV2 from './IELTSDashboardV2';
import { themeColors } from '../components/IELTS/styles/theme';

const ANNOUNCEMENT_BAR_HEIGHT = 40;
const MOCK_GROUPS_PER_PAGE = 6;

const getActiveMockList = () => {
  const headings = Array.from(document.querySelectorAll('h5'));
  const heading = headings.find((element) => {
    const text = element.textContent?.trim();
    return text === 'Sectional Mock Test' || text === 'Full Mock Test';
  });

  if (!heading) return null;

  const mainCard = heading.closest('.MuiPaper-root');
  if (!mainCard) return null;

  const viewKey = heading.textContent?.includes('Full') ? 'full' : 'sectional';
  const children = Array.from(mainCard.children);
  const groups = children.filter((child) => (
    child.classList.contains('MuiPaper-root')
    && child.getAttribute('data-ielts-pagination-slot') === null
    && child.querySelector('.MuiChip-root')
  ));

  return { groups, mainCard, viewKey };
};

const getSlot = (mainCard, position) => (
  Array.from(mainCard.children).find(
    (child) => child.getAttribute('data-ielts-pagination-slot') === position
  )
);

const getStoredPage = (searchParams, viewKey) => {
  const page = Number(searchParams.get(`${viewKey}Page`));
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const removePaginationSlots = () => {
  document.querySelectorAll('[data-ielts-pagination-slot]').forEach((slot) => slot.remove());
};

const IeltsDashboardPaginationEnhancer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsSignature = searchParams.toString();
  const [pages, setPages] = useState(() => ({
    sectional: getStoredPage(searchParams, 'sectional'),
    full: getStoredPage(searchParams, 'full'),
  }));
  const [pagination, setPagination] = useState({
    bottomSlot: null,
    page: 1,
    topSlot: null,
    totalPages: 1,
    viewKey: null,
  });
  const frameRef = useRef(null);
  const activeListRef = useRef({ mainCard: null, viewKey: null });

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsSignature);
    const nextPages = {
      sectional: getStoredPage(currentParams, 'sectional'),
      full: getStoredPage(currentParams, 'full'),
    };

    setPages((current) => (
      current.sectional === nextPages.sectional && current.full === nextPages.full
        ? current
        : nextPages
    ));
  }, [searchParamsSignature]);

  const updatePage = (viewKey, page) => {
    setPages((current) => ({ ...current, [viewKey]: page }));

    const nextParams = new URLSearchParams(searchParamsSignature);
    const paramName = `${viewKey}Page`;
    if (page <= 1) {
      nextParams.delete(paramName);
    } else {
      nextParams.set(paramName, String(page));
    }

    const nextSignature = nextParams.toString();
    if (nextSignature !== searchParamsSignature) {
      setSearchParams(nextParams, { replace: true });
    }
  };

  useEffect(() => {
    const applyPagination = () => {
      const activeList = getActiveMockList();

      if (!activeList || activeList.groups.length <= MOCK_GROUPS_PER_PAGE) {
        removePaginationSlots();
        activeListRef.current = { mainCard: null, viewKey: null };
        activeList?.groups.forEach((group) => {
          group.style.display = '';
        });
        setPagination((current) => (
          current.viewKey === null
            ? current
            : { bottomSlot: null, page: 1, topSlot: null, totalPages: 1, viewKey: null }
        ));
        return;
      }

      const { groups, mainCard, viewKey } = activeList;
      const switchedMockList = activeListRef.current.viewKey !== viewKey
        || activeListRef.current.mainCard !== mainCard;

      if (switchedMockList) {
        removePaginationSlots();
        activeListRef.current = { mainCard, viewKey };
      }

      const totalPages = Math.ceil(groups.length / MOCK_GROUPS_PER_PAGE);
      const requestedPage = pages[viewKey] || 1;
      const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);

      if (requestedPage !== currentPage) {
        setPages((current) => ({ ...current, [viewKey]: currentPage }));
        const nextParams = new URLSearchParams(searchParamsSignature);
        const paramName = `${viewKey}Page`;
        if (currentPage <= 1) {
          nextParams.delete(paramName);
        } else {
          nextParams.set(paramName, String(currentPage));
        }
        if (nextParams.toString() !== searchParamsSignature) {
          setSearchParams(nextParams, { replace: true });
        }
        return;
      }

      const firstVisibleIndex = (currentPage - 1) * MOCK_GROUPS_PER_PAGE;
      const lastVisibleIndex = firstVisibleIndex + MOCK_GROUPS_PER_PAGE;
      groups.forEach((group, index) => {
        group.style.display = index >= firstVisibleIndex && index < lastVisibleIndex ? '' : 'none';
      });

      let topSlot = getSlot(mainCard, 'top');
      if (!topSlot) {
        topSlot = document.createElement('div');
        topSlot.setAttribute('data-ielts-pagination-slot', 'top');
        mainCard.insertBefore(topSlot, groups[0]);
      }

      let bottomSlot = getSlot(mainCard, 'bottom');
      if (!bottomSlot) {
        bottomSlot = document.createElement('div');
        bottomSlot.setAttribute('data-ielts-pagination-slot', 'bottom');
        mainCard.insertBefore(bottomSlot, groups[groups.length - 1].nextSibling);
      }

      setPagination((current) => {
        const hasSameState = current.viewKey === viewKey
          && current.page === currentPage
          && current.totalPages === totalPages
          && current.topSlot === topSlot
          && current.bottomSlot === bottomSlot;
        return hasSameState
          ? current
          : { bottomSlot, page: currentPage, topSlot, totalPages, viewKey };
      });
    };

    const schedulePagination = () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(applyPagination);
    };

    const observer = new MutationObserver(schedulePagination);
    observer.observe(document.body, { childList: true, subtree: true });
    schedulePagination();

    return () => {
      observer.disconnect();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      removePaginationSlots();
    };
  }, [pages, searchParamsSignature, setSearchParams]);

  const renderControls = (slot) => createPortal(
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        my: 1.5,
      }}
    >
      <Pagination
        count={pagination.totalPages}
        page={pagination.page}
        onChange={(_, value) => updatePage(pagination.viewKey, value)}
        color="primary"
        shape="rounded"
        showFirstButton
        showLastButton
        siblingCount={1}
        boundaryCount={1}
        sx={{
          '& .MuiPaginationItem-root': {
            fontWeight: 800,
          },
          '& .Mui-selected': {
            backgroundColor: `${themeColors.primary} !important`,
            color: '#fff',
          },
        }}
      />
    </Box>,
    slot
  );

  if (!pagination.viewKey || !pagination.topSlot || !pagination.bottomSlot) return null;

  return (
    <>
      {renderControls(pagination.topSlot)}
      {renderControls(pagination.bottomSlot)}
    </>
  );
};

const IELTSDashboardWithAnnouncements = () => {
  const [barExists, setBarExists] = useState(false);
  const [adminBarExists, setAdminBarExists] = useState(false);
  const announcementOffset = (barExists ? ANNOUNCEMENT_BAR_HEIGHT : 0) + (adminBarExists ? ANNOUNCEMENT_BAR_HEIGHT : 0);

  return (
    <Box
      sx={{
        '& #navbar': {
          top: { xs: 'auto', md: `${announcementOffset}px` },
        },
        '& #navbar + div': {
          paddingTop: `calc(82px + ${announcementOffset}px)`,
          '@media (max-width: 900px)': {
            paddingTop: `calc(72px + ${announcementOffset}px)`,
          },
        },
      }}
    >
      <PortalAnnouncements setBarExists={setBarExists} setAdminBarExists={setAdminBarExists} />
      <IELTSDashboardV2 />
      <IeltsDashboardPaginationEnhancer />
    </Box>
  );
};

export default IELTSDashboardWithAnnouncements;
